import { useCallback, useEffect, useMemo, useState } from "react";
import { AUTH_STATUS } from "../hooks/useAuthManager";

export const ROUTES = {
  LOGIN: "/login",
  LOGOUT: "/logout",
  PROJECTS: "/projects",
  PROJECT_CREATE: "/projects/new",
  PROJECT_STRUCTURE: "/project-structure",
  PROJECT_EDIT_PREFIX: "/projects/",
  PROFILE: "/profile",
  SETTINGS: "/settings",
  BASES: "/bases",
  BASES_MATERIAL: "/bases-material",
  BASES_EQUIPMENT: "/bases-equipment",
  BASES_LABOUR: "/bases-labour",
  BOQ: "/boq",
  SHEET_LOOKUP: "/sheet-lookup",
  LOG: "/log",
  EXPORT_PDF: "/export-pdf",
  OPEN_SHEETS: "/open-sheets",
  DELETE_PROJECT: "/delete-project",
  APP_ERRORS: "/app-errors",
  RENDER_BOQ: "/render-boq",
  BASES_PRICES: "/bases-prices",
  WORK_BLOCKS: "/work-blocks",
  AUTO_EVALUATE: "/auto-evaluate",
  CONTROL_PANEL: "/control-panel",
  ANALYTICS: "/analytics",
  IMPORT_DATA: "/import-data",
  EXPORT_DATA: "/export-data",
  INDEXATION: "/indexation",
  TOOLS: "/tools",
  UPDATE: "/update",
};

// Routes that work without signing in.
const PUBLIC_ROUTES = new Set([ROUTES.LOGIN, ROUTES.UPDATE]);

const getHashPath = () => {
  const raw = window.location.hash || "";
  const trimmed = raw.replace(/^#/, "").trim();
  if (!trimmed) return ROUTES.LOGIN;
  return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
};

const normalize = (path) => {
  if (!path) return ROUTES.LOGIN;
  const cleaned = path.startsWith("/") ? path : `/${path}`;
  return cleaned.replace(/\s+/g, "");
};

const isEditRoute = (path) =>
  path.startsWith(ROUTES.PROJECT_EDIT_PREFIX) && path.length > ROUTES.PROJECT_EDIT_PREFIX.length;

const getProjectIdFromPath = (path) => {
  if (!isEditRoute(path)) return null;
  const suffix = path.substring(ROUTES.PROJECT_EDIT_PREFIX.length);
  const [id] = suffix.split("/");
  return id || null;
};

export function useHashRouter(authStatus) {
  const [path, setPath] = useState(() => getHashPath());

  useEffect(() => {
    const handler = () => {
      setPath(getHashPath());
    };

    window.addEventListener("hashchange", handler);
    return () => window.removeEventListener("hashchange", handler);
  }, []);

  const navigate = useCallback(
    (next) => {
      const normalized = normalize(next);
      if (normalized === path) return;
      window.location.hash = `#${normalized}`;
    },
    [path]
  );

  useEffect(() => {
    const normalized = normalize(path);
    if (normalized !== path) {
      setPath(normalized);
    }
  }, [path]);

  useEffect(() => {
    if (authStatus === AUTH_STATUS.AUTHENTICATED && path === ROUTES.LOGIN) {
      navigate(ROUTES.PROJECTS);
    }

    if (authStatus === AUTH_STATUS.ANONYMOUS && !PUBLIC_ROUTES.has(path)) {
      navigate(ROUTES.LOGIN);
    }
  }, [authStatus, path, navigate]);

  const route = useMemo(() => {
    if (path === ROUTES.LOGIN) {
      return { name: "login" };
    }

    if (path === ROUTES.LOGOUT) {
      return { name: "logout" };
    }

    if (path === ROUTES.UPDATE) {
      return { name: "update" };
    }

    if (path === ROUTES.PROJECTS) {
      return { name: "projects" };
    }

    if (path === ROUTES.PROJECT_CREATE) {
      return { name: "project-create" };
    }

    if (path === ROUTES.PROJECT_STRUCTURE) {
      return { name: "project-structure" };
    }

    if (path === ROUTES.PROFILE) {
      return { name: "profile" };
    }

    if (path === ROUTES.SETTINGS) {
      return { name: "settings" };
    }

    if (path === ROUTES.BASES) {
      return { name: "bases" };
    }

    if (path === ROUTES.BASES_MATERIAL) {
      return { name: "bases-material" };
    }

    if (path === ROUTES.BASES_EQUIPMENT) {
      return { name: "bases-equipment" };
    }

    if (path === ROUTES.BASES_LABOUR) {
      return { name: "bases-labour" };
    }

    if (path === ROUTES.BOQ) {
      return { name: "boq" };
    }

    if (path === ROUTES.SHEET_LOOKUP) {
      return { name: "sheet-lookup" };
    }

    if (path === ROUTES.LOG) {
      return { name: "log" };
    }

    if (path === ROUTES.EXPORT_PDF) {
      return { name: "export-pdf" };
    }

    if (path === ROUTES.OPEN_SHEETS) {
      return { name: "open-sheets" };
    }

    if (path === ROUTES.DELETE_PROJECT) {
      return { name: "delete-project" };
    }

    if (path === ROUTES.APP_ERRORS) {
      return { name: "app-errors" };
    }

    if (path === ROUTES.RENDER_BOQ) {
      return { name: "render-boq" };
    }

    if (path === ROUTES.BASES_PRICES) {
      return { name: "bases-prices" };
    }

    if (path === ROUTES.WORK_BLOCKS) {
      return { name: "work-blocks" };
    }

    if (path === ROUTES.AUTO_EVALUATE) {
      return { name: "auto-evaluate" };
    }

    if (path === ROUTES.CONTROL_PANEL) {
      return { name: "control-panel" };
    }

    if (path === ROUTES.ANALYTICS) {
      return { name: "analytics" };
    }

    if (path === ROUTES.IMPORT_DATA) {
      return { name: "import-data" };
    }

    if (path === ROUTES.EXPORT_DATA) {
      return { name: "export-data" };
    }

    if (path === ROUTES.TOOLS) {
      return { name: "tools" };
    }

    if (path === ROUTES.INDEXATION) {
      return { name: "indexation" };
    }

    const projectId = getProjectIdFromPath(path);
    if (projectId) {
      return { name: "project-edit", params: { projectId } };
    }

    return { name: "unknown" };
  }, [path]);

  return useMemo(
    () => ({
      path,
      route,
      navigate,
    }),
    [path, route, navigate]
  );
}
